import React, { useState, useEffect, useRef } from 'react';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  containerClassName?: string;
  onLoad?: () => void;
  onError?: () => void;
}

const LazyImage: React.FC<LazyImageProps> = ({ 
  src, 
  alt, 
  className = '', 
  containerClassName = '',
  onLoad: propOnLoad,
  onError: propOnError
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [imageKey, setImageKey] = useState(0);
  const imageRef = useRef<HTMLDivElement>(null);

    // Reset when src changes
  useEffect(() => {
    setError(false);
    setIsLoading(true);
    setRetryCount(0);
    setImageKey(0);
  }, [src]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading images when they're 50px from viewport
        threshold: 0.1
      }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleLoad = () => {
    // console.log(`✅ Image loaded successfully: ${alt}`);
    setIsLoading(false);
    setError(false);
    propOnLoad?.();
  };

  const handleError = () => {
    console.log(`❌ Image failed to load (attempt ${retryCount + 1}): ${src}`);
    // Retry logic with exponential backoff
    if (retryCount < 2) { // Retry up to 2 times
      const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s delays
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setImageKey(prev => prev + 1); // FIXED: Just change key to force re-render
      }, delay);
    } else {
      setError(true);
      setIsLoading(false);
      propOnError?.();
    }
  };


  return (
    <div 
      ref={imageRef}
      className={`relative overflow-hidden ${containerClassName}`}
      data-loading={isLoading}
      data-error={error}
    >
      {/* Loading skeleton */}
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse">
          <div className="h-full w-full flex items-center justify-center">
            <span className="text-gray-400 text-sm">
              {retryCount > 0 ? `Retrying... (${retryCount}/2)` : 'Loading...'}
            </span>
          </div>
        </div>
      )}
      
      {/* Only render image when it should load */}
      {shouldLoad && (
        <img
          key={imageKey} // Force re-render on retry
          src={src}
          alt={alt}
          className={`
            ${className}
            ${isLoading ? 'opacity-0' : 'opacity-100'}
            transition-opacity duration-300
          `}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <span className="text-gray-400 text-sm">Failed to load image</span>
        </div>
      )}
    </div>
  );
};

export default LazyImage;